<template>
    <base-form
        ref="form"
        v-model="form"
        :api="apiClientType.RESIDENTIAL_CANTEENS"
        :mapper="mapperType.RESIDENTIAL_CANTEENS_FORM"
        :entity-id="entityId"
        :title="title"
        :disabled="disabled"
        @created="created"
        @updated="updated"
        @cancelled="cancelled"
        @deleted="deleted"
    >
        <!-- RESIDENTIAL CANTEENS -->

        <v-col cols="12">
            <v-card color="grey lighten-5" flat>
                <v-card-text>
                    <div class="row">
                        <div class="col-lg-12">
                            <!-- residential canteen name -->
                            <text-input
                                v-model="form.name"
                                v-validate="'required|min:1|max:100|space'"
                                name="name"
                                label="residential_canteen.name"
                                :error-messages="errors.collect('name')"
                                :data-vv-as="$t('residential_canteen.name')"
                            ></text-input>
                        </div>
                        <div class="col-lg-6">
                            <date-picker-input
                                v-model="form.cutoffTime"
                                v-validate="'space'"
                                :label="$t('residential_canteen.cutoff_time')"
                                name="cutoffTime"
                                :error-messages="errors.collect('cutoffTime')"
                                :data-vv-as="
                                    $t('residential_canteen.cutoff_time')
                                "
                                :min="entityId ? null : minDate"
                            ></date-picker-input>
                        </div>
                        <div class="col-lg-6">
                            <date-picker-input
                                v-model="form.deliveryTime"
                                v-validate="'space'"
                                :label="$t('residential_canteen.delivery_time')"
                                name="deliveryTime"
                                :min="form.cutoffTime"
                                :error-messages="errors.collect('deliveryTime')"
                                :data-vv-as="
                                    $t('residential_canteen.delivery_time')
                                "
                            ></date-picker-input>
                        </div>
                        <div class="col-lg-12">
                            <!-- Residential Canteen addresses -->
                            <entity-select-input
                                v-model="form.address"
                                v-validate="'required'"
                                label="residential_canteen.address"
                                name="address"
                                :entity="
                                    selectApiClientType.RESIDENTIAL_CANTEEN_ADDRESS
                                "
                                :data-vv-as="$t('residential_canteen.address')"
                                :error-messages="errors.collect('address')"
                            ></entity-select-input>
                        </div>
                        <div class="col-lg-12">
                            <!-- Residential Canteen related restaurants -->
                            <entity-select-input
                                v-model="form.relatedRestaurants"
                                label="residential_canteen.related_restaurants"
                                name="relatedRestaurants"
                                :entity="
                                    selectApiClientType.RESIDENTIAL_CANTEEN_RESTAURANT
                                "
                                :query-params="{
                                    residential_canteen_address_id: getAddressIdFromIri(
                                        form.address
                                    ),
                                }"
                                :data-vv-as="
                                    $t(
                                        'residential_canteen.related_restaurants'
                                    )
                                "
                                :error-messages="
                                    errors.collect('relatedRestaurants')
                                "
                                multi-select
                                :request-disabled="!form.address"
                                :disabled="!form.address"
                            ></entity-select-input>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-lg-12">
                            <checkbox-input
                                v-model="form.isRecurring"
                                label="residential_canteen.is_recurring"
                                name="isRecurring"
                            ></checkbox-input>
                        </div>
                        <div class="col-lg-12">
                            <text-input
                                v-model="form.recurringOccasionLimit"
                                v-validate="'min:1|max:100|space'"
                                name="recurringOccasionLimit"
                                label="residential_canteen.recurring_occasion_limit"
                                :error-messages="
                                    errors.collect('recurringOccasionLimit')
                                "
                                :data-vv-as="
                                    $t(
                                        'residential_canteen.recurring_occasion_limit'
                                    )
                                "
                                :disabled="!form.isRecurring"
                                type="number"
                            ></text-input>
                            <text-input
                                v-model="form.recurringDayLimit"
                                v-validate="'min:1|max:100|space'"
                                name="recurringDayLimit"
                                label="residential_canteen.recurring_day_limit"
                                :error-messages="
                                    errors.collect('recurringDayLimit')
                                "
                                :data-vv-as="
                                    $t(
                                        'residential_canteen.recurring_day_limit'
                                    )
                                "
                                :disabled="!form.isRecurring"
                                type="number"
                            ></text-input>
                            <text-input
                                v-model="form.pickupOffset"
                                v-validate="'min:1|max:100|space'"
                                name="pickupOffset"
                                label="residential_canteen.pickup_offset"
                                :error-messages="errors.collect('pickupOffset')"
                                :data-vv-as="
                                    $t('residential_canteen.pickup_offset')
                                "
                                type="number"
                            ></text-input>
                            <text-input
                                v-model="form.sendNotificationBeforeCutoff"
                                v-validate="'min:1|max:100|space'"
                                name="sendNotificationBeforeCutoff"
                                label="labels.send_notification_before_cutoff"
                                :error-messages="
                                    errors.collect(
                                        'sendNotificationBeforeCutoff'
                                    )
                                "
                                :data-vv-as="
                                    $t('labels.send_notification_before_cutoff')
                                "
                                type="number"
                            ></text-input>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
    </base-form>
</template>

<script>
import { mapGetters } from 'vuex'
import configType from '@/store/type/configType'
import routeType from '@/router/routeType'
import FormMixin from '@/components/mixins/FormMixin'
import SelectApiClientType from '@/api/SelectApiClientType'
import IdFromIri from '@/services/IdFromIri'

export default {
    mixins: [FormMixin],
    data() {
        return {
            form: {
                name: '',
                value: 0,
                minimumPrice: 0,
                maximumPrice: null,
                usageLimit: null,
                userUsageLimit: 0,
                dateFrom: null,
                dateUntil: null,
                isPercent: false,
                appliesToDelivery: false,
                restaurant: [],
                paymentMethodList: [],
                relatedRestaurants: [],
                sendNotificationBeforeCutoff: 30,
            },
            selectApiClientType: SelectApiClientType,
            entity: 'Residential Canteen',
            editRoute: routeType.RESIDENTIAL_CANTEENS_EDIT,
            listRoute: routeType.RESIDENTIAL_CANTEENS,
        }
    },
    computed: {
        ...mapGetters({
            currencyCode: configType.getters.CURRENCY_CODE,
        }),
        title() {
            return this.entityId
                ? this.$t('residential_canteen.edit_page_title', {
                      entityName: `${this.form?.name}`,
                  })
                : this.$t('residential_canteen.create_page_title')
        },
        minDate() {
            return this.$date().format('YYYY-MM-DD')
        },
    },
    methods: {
        getAddressIdFromIri(addressIri) {
            if (addressIri) return IdFromIri.getId(addressIri)
        },
        editRedirectParams: (data) => ({ entityId: data.id }),
    },
}
</script>
